// ─────────────────────────────────────────────
// src/pages/ProgramsPage.jsx
// ─────────────────────────────────────────────
import { useEffect } from "react";
import { StarField, GlassCard, Btn, SectionLabel, SectionTitle } from "../components/UI";
import Footer from "../components/Footer";
import { LEARNING_AREAS } from "../data/constants";

export default function ProgramsPage({ setPage }) {
  useEffect(() => {
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = '__course-schema';
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Course",
      "name": "Mars Academy STEM Enrichment Program",
      "description": "A comprehensive STEM program, offered before and after school, for elementary and middle school students in Fairfax County and Northern Virginia, covering coding with Scratch, artificial intelligence, internet safety, cloud computing, computer basics, and digital infrastructure. 6 in-person sessions, 2 hours each.",
      "provider": {
        "@type": "EducationalOrganization",
        "name": "Mars Academy",
        "url": "https://www.marsacademy.ai/"
      },
      "educationalLevel": "Elementary School, Middle School",
      "teaches": [
        "Computer Basics & Navigation",
        "Internet Safety & Cybersecurity",
        "Programming with Scratch",
        "Artificial Intelligence",
        "Cloud Computing",
        "Digital Infrastructure"
      ],
      "hasCourseInstance": [{
        "@type": "CourseInstance",
        "courseMode": "in-person",
        "duration": "PT2H",
        "courseSchedule": {
          "@type": "Schedule",
          "repeatCount": 6,
          "repeatFrequency": "weekly"
        },
        "offers": {
          "@type": "Offer",
          "price": "249",
          "priceCurrency": "USD"
        }
      }]
    });
    document.head.appendChild(script);
    return () => document.getElementById('__course-schema')?.remove();
  }, []);
  const details = [
    ["📅", "Duration",     "~6 classes / session"],
    ["📆", "Frequency",    "1 class / week"  ],
    ["⏱️", "Class Length", "~2 hours"        ],
    ["🏫", "Format",       "In-person"       ],
    ["💻", "Equipment",    "Laptops provided"],
    ["🆓", "Scratch",      "Free account needed"],
  ];

  return (
    <div>
      {/* Header */}
      <section style={{ padding:"8rem 2rem 4rem", textAlign:"center", position:"relative", overflow:"hidden" }}>
        <StarField count={40} />
        <div style={{ position:"relative", zIndex:2, maxWidth:720, margin:"0 auto" }}>
          <SectionLabel color="#FF6B6B">The Program</SectionLabel>
          <h1 style={{ fontFamily:"'Fredoka One',cursive", fontSize:"clamp(2rem,5vw,3.4rem)", lineHeight:1.2, marginBottom:"1rem" }}>
            One Program. Six Skills. Total Launch. 🛸
          </h1>
          <p style={{ color:"rgba(255,255,255,0.55)", fontSize:"1rem", fontWeight:600, lineHeight:1.75, maxWidth:580, margin:"0 auto" }}>
            A single, comprehensive program, offered before and after school, covering all essential technology skills every elementary and middle school student needs. Typically 6 classes per session · 1 class/week · ~2 hours per class (exact schedule varies by school).
          </p>
        </div>
      </section>

      {/* Details banner */}
      <section style={{ maxWidth:900, margin:"0 auto 2rem", padding:"0 2rem" }}>
        <div style={{ background:"linear-gradient(135deg,rgba(255,217,61,0.1),rgba(155,92,246,0.1))", border:"1.5px solid rgba(255,217,61,0.25)", borderRadius:22, padding:"1.8rem 2.2rem", }}>
          <div style={{ display:"flex", flexWrap:"wrap", gap:"2rem", justifyContent:"center", alignItems:"center" }}>
            {details.map(([ic, label, val]) => (
              <div key={label} style={{ textAlign:"center", minWidth:90 }}>
                <div style={{ fontSize:"1.5rem", marginBottom:4 }}>{ic}</div>
                <div style={{ fontWeight:800, fontSize:"0.75rem", color:"rgba(255,255,255,0.45)", marginBottom:2, textTransform:"uppercase", letterSpacing:"0.06em" }}>{label}</div>
                <div style={{ fontFamily:"'Fredoka One',cursive", fontSize:"1rem", color:"#FFD93D" }}>{val}</div>
              </div>
            ))}
          </div>
          <div style={{ borderTop:"1px solid rgba(255,255,255,0.1)", marginTop:"1.6rem", paddingTop:"0.9rem", display:"flex", justifyContent:"center", alignItems:"center", gap:"0.5rem" }}>
            <span style={{ fontSize:"0.9rem" }}>ℹ️</span>
            <span style={{ color:"rgba(255,255,255,0.5)", fontSize:"0.78rem", fontWeight:600 }}>
              Session duration and class times vary by school
            </span>
          </div>
        </div>
      </section>

      {/* Topics grid */}
      <section style={{ maxWidth:1100, margin:"0 auto", padding:"2rem 2rem 5rem" }}>
        <div style={{ textAlign:"center", marginBottom:"2.2rem" }}>
          <SectionLabel color="#9B5CF6">What's Covered</SectionLabel>
          <SectionTitle center>Topics Included in Every Session</SectionTitle>
        </div>
        <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(300px,1fr))", gap:"1.4rem" }}>
          {LEARNING_AREAS.map((a, i) => (
            <GlassCard key={i} style={{ display:"flex", gap:"1.2rem", alignItems:"flex-start" }}>
              <div style={{ width:56, height:56, borderRadius:15, background:a.color+"18", border:`2px solid ${a.color}38`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:"1.75rem", flexShrink:0 }}>{a.icon}</div>
              <div>
                <h3 style={{ fontFamily:"'Fredoka One',cursive", fontSize:"1.15rem", marginBottom:"0.4rem", color:a.color }}>{a.title}</h3>
                <p style={{ color:"rgba(255,255,255,0.5)", fontSize:"0.87rem", lineHeight:1.65, fontWeight:600 }}>{a.desc}</p>
              </div>
            </GlassCard>
          ))}
        </div>
        <div style={{ textAlign:"center", marginTop:"3rem" }}>
          <Btn onClick={() => setPage("login")} variant="primary" style={{ fontSize:"1rem", padding:"14px 40px" }}>🚀 Sign Up Now</Btn>
          <p style={{ color:"rgba(255,255,255,0.4)", fontSize:"0.85rem", fontWeight:600, marginTop:"1.4rem" }}>
            Wondering how the before- and after-school schedule works?{" "}
            <button onClick={() => setPage("after-school-programs")} style={{ background:"none", border:"none", color:"#FFD93D", fontFamily:"inherit", fontWeight:800, fontSize:"0.85rem", cursor:"pointer", padding:0 }}>
              See how our programs run →
            </button>
          </p>
        </div>
      </section>

      <Footer setPage={setPage} />
    </div>
  );
}
